import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CampaignsBulkStatusEnum, TypeCampaignEnum } from '@/types';
import { findPermission, UserState } from '@/composables/User';
import { store } from '@/store';
import { showToastError } from '@/helpers';
import {
  activateCampaign,
  setAnalyticsMode,
} from '@/composables/AutomatedScenarios';
import { getBulkCampaignStatus } from '@/composables/workflows/CampaignBulk';
import { getNotConfiguredServices } from '@/composables/shop/MyShopParameters';
import { getShopsCredits } from '@/composables/credits/Credits';
import { fetchFlowStructure, updateBulkStatus } from '@/composables/flows/useFlowApi';
import type { RowActionOption, GroupedActionOption, SelectionInfo } from '@/components/table-rest-api/types';
import { isWorkshopAccount } from '@/composables/WorkshopMode';
import type { FlowStructureDto, FlowStructureSettingsRaw } from '@/types/flow-structure';
import type { FlowSettingsBulk } from '@/types/flow-settings';
import { getDefaultBulkSettings } from '@/types/flow-settings';
import { i18n } from '@/i18n';
import type { BulkCampaignData, ActionComponentRef, CampaignActionPayload } from './types';

// Statuses that allow preview (analytics view)
const previewStatuses = [
  CampaignsBulkStatusEnum.SENT,
  CampaignsBulkStatusEnum.SENDING,
  CampaignsBulkStatusEnum.SUSPENDED,
  CampaignsBulkStatusEnum.SUSPENDED_BY_USER,
  CampaignsBulkStatusEnum.ARCHIVED,
];

// Statuses that allow editing
export const bulkEditStatuses = [
  CampaignsBulkStatusEnum.DRAFT,
  CampaignsBulkStatusEnum.BEING_EDITED,
  CampaignsBulkStatusEnum.SCHEDULED,
];

// Statuses that allow deletion
const deleteStatuses = [
  CampaignsBulkStatusEnum.DRAFT,
  CampaignsBulkStatusEnum.SCHEDULED,
];

// Statuses that allow archiving
const archiveStatuses = [
  CampaignsBulkStatusEnum.SENT,
  CampaignsBulkStatusEnum.SUSPENDED,
  CampaignsBulkStatusEnum.SUSPENDED_BY_USER,
];

// Statuses that allow suspending (stop sending)
const suspendStatuses = [
  CampaignsBulkStatusEnum.SENDING,
];

/**
 * Extract operators from flow structure steps
 */
const getOperatorsFromFlowStructure = (flowData: FlowStructureDto): string[] => {
  if (!flowData.steps || !Array.isArray(flowData.steps)) {
    return [];
  }
  return flowData.steps
    .map((step) => step.parameters?.id)
    .filter((id): id is string => !!id);
};

/**
 * Transform raw API settings to internal FlowSettingsBulk format
 */
const transformApiSettingsToInternalBulk = (
  rawSettings: FlowStructureSettingsRaw | undefined,
  campaignName: string,
): FlowSettingsBulk => {
  const defaults = getDefaultBulkSettings(campaignName);

  if (!rawSettings) {
    return defaults;
  }

  // The persisted JSON stores binaries as strings (legacy PHP origin) — coerce
  // explicitly so the editor's strict `=== 1` comparisons match.
  const toBinary = (v: unknown, fallback: 0 | 1): 0 | 1 => {
    if (v == null) return fallback;
    return Number(v) === 1 ? 1 : 0;
  };

  return {
    ...defaults,
    name: campaignName,
    legend: rawSettings.legend ?? defaults.legend,
    commercial_campaign: toBinary(
      rawSettings.commercial_campaign,
      defaults.commercial_campaign,
    ),
    track_sales: rawSettings.track_sales ?? defaults.track_sales,
  };
};

/**
 * Open bulk campaign in flow editor with all necessary checks
 * This is exported separately to be reused in campaign creation flows
 * @param isPreviewMode - If true, opens in preview mode (analytics only, no editing)
 */
export const openBulkCampaignEditor = async (
  campaignId: number,
  campaignStatus: CampaignsBulkStatusEnum,
  campaignName: string,
  onClose?: () => void,
  onRefresh?: () => void,
  isPreviewMode = false,
): Promise<void> => {
  const translation: any = await i18n;
  const idShop = UserState.activeShop?.id ?? 0;

  store.commit('general/showTheSpinner');

  try {
    // Fetch flow structure first
    const flowData: FlowStructureDto = await fetchFlowStructure(TypeCampaignEnum.BULK, campaignId);
    console.log('flowData', flowData);
    // Transform API settings to internal format
    if (flowData.settings && flowData.settings.name && campaignName === '') {
      // eslint-disable-next-line no-param-reassign
      campaignName = flowData.settings.name;
    }
    // Validate status
    if (!flowData.status || flowData.status !== campaignStatus) {
      await showToastError(translation.global.t('errorMessages.GENERIC_ERROR'));
      if (onRefresh) onRefresh();
      store.commit('general/hideTheSpinner');
      return;
    }

    // Skip edit status checks in preview mode
    if (!isPreviewMode) {
      // Check if status allows editing
      if (!bulkEditStatuses.includes(flowData.status as CampaignsBulkStatusEnum)) {
        await showToastError(translation.global.t('errorMessages.GENERIC_ERROR'));
        if (onRefresh) onRefresh();
        store.commit('general/hideTheSpinner');
        return;
      }

      // Check if campaign is being edited by another user
      if (flowData.status === CampaignsBulkStatusEnum.BEING_EDITED) {
        if (flowData.dateModification) {
          const now = Date.now();
          const dateCampaign = new Date(flowData.dateModification).getTime();
          const seconds = (now - dateCampaign) / 1000;

          // If last modification was less than 15 seconds ago, another user is editing
          if (seconds < 15) {
            await showToastError(translation.global.t('campaignsBulkList.actions.beingEditedByAnotherUser'));
            store.commit('general/hideTheSpinner');
            return;
          }
        }
      }
    }

    // Transform API settings to internal format
    const settings = transformApiSettingsToInternalBulk(flowData.settings, campaignName);

    // Extract operators from flow structure steps
    const operators = getOperatorsFromFlowStructure(flowData);

    // Check for services that need configuration (skip in preview mode)
    const notConfiguredServices = isPreviewMode 
      ? [] 
      : await getNotConfiguredServices(idShop, ['editScenarioCampaign', ...operators]);

    const openFlowEditor = async () => {
      // Set status to BEING_EDITED before opening (skip in preview mode)
      if (!isPreviewMode) {
        try {
          await updateBulkStatus(campaignId, CampaignsBulkStatusEnum.BEING_EDITED);
          // Update status in flowData before opening
          flowData.status = CampaignsBulkStatusEnum.BEING_EDITED;
        } catch (error) {
          console.error('Failed to update bulk status to BEING_EDITED:', error);
        }
      }

      store.dispatch('flowEditor/openFlow', {
        id: campaignId,
        flowData,
        type: TypeCampaignEnum.BULK,
        name: campaignName,
        settings,
        isPreviewMode,
      });
      if (onClose) onClose();
      store.commit('general/hideTheSpinner');
    };

    if (notConfiguredServices.length) {
      store.commit('general/setNotConfiguredServices', notConfiguredServices);
      store.commit('general/setIsServicesParametersModalVisible', true);
      store.commit('general/setServiceParameterSuccessAction', openFlowEditor);
      store.commit('general/hideTheSpinner');
    } else {
      openFlowEditor();
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : translation.global.t('errorMessages.GENERIC_ERROR');
    await showToastError(errorMessage);
    store.commit('general/hideTheSpinner');
  }
};

/**
 * Options for useBulkCampaignsActions composable
 */
interface UseBulkCampaignsActionsOptions {
  duplicateRef: Ref<ActionComponentRef | null>;
  deleteRef: Ref<ActionComponentRef | null>;
  archiveRef: Ref<ActionComponentRef | null>;
  suspendRef: Ref<ActionComponentRef | null>;
  onExport?: (selection: SelectionInfo) => void;
  onPublishToGallery?: (selection: SelectionInfo) => void;
  onRefresh?: () => void;
  showNeedOfferModal?: Ref<boolean>;
  confirm?: ReturnType<typeof import('primevue/useconfirm').useConfirm>;
}

/**
 * Composable that provides row and grouped actions for bulk campaigns management
 * Actions are configured based on campaign status
 */
export default function useBulkCampaignsActions(options: UseBulkCampaignsActionsOptions) {
  const { t } = useI18n();
  const idShop = UserState.activeShop?.id ?? 0;
  const { confirm } = options;

  /**
   * Check if campaign can be previewed (analytics view)
   */
  const canPreview = (status: CampaignsBulkStatusEnum): boolean => previewStatuses.includes(status);

  /**
   * Check if campaign can be edited
   */
  const canEdit = (status: CampaignsBulkStatusEnum): boolean => bulkEditStatuses.includes(status);

  /**
   * Check if campaign can be deleted
   */
  const canDelete = (status: CampaignsBulkStatusEnum): boolean => deleteStatuses.includes(status);

  /**
   * Check if campaign can be archived
   */
  const canArchive = (status: CampaignsBulkStatusEnum): boolean => archiveStatuses.includes(status);

  /**
   * Check if campaign can be suspended
   */
  const canSuspend = (status: CampaignsBulkStatusEnum): boolean => suspendStatuses.includes(status);

  /**
   * Build payload for action components
   */
  const buildPayload = (campaign: BulkCampaignData): CampaignActionPayload => ({
    campaignId: campaign.id,
    campaignName: campaign.name,
    campaignStatus: campaign.status,
  });

  /**
   * Handle preview action - opens campaign in new flow editor with preview mode (analytics only)
   */
  const handlePreview = async (campaign: BulkCampaignData) => {
    if (!campaign?.id) return;
    await openBulkCampaignEditor(campaign.id, campaign.status, campaign.name, undefined, options.onRefresh, true);
  };

  /**
   * Handle edit action - opens campaign editor (new flow editor)
   */
  const handleEdit = async (campaign: BulkCampaignData) => {
    if (!campaign?.id) return;

    // Check active offer/plan
    const activePlan = UserState.activeOffer
      || (UserState.hasOfferV3 && UserState.offerV3 && ['2', '3'].includes(UserState.offerV3.bundle_options) && UserState.offerV3.max_newsletter > 0);
    const newsletterCredits = await getShopsCredits(idShop, 'newsletter', true);

    if (!activePlan && (!newsletterCredits || newsletterCredits === 0)) {
      if (options.showNeedOfferModal?.value !== undefined) {
        // eslint-disable-next-line no-param-reassign
        options.showNeedOfferModal.value = true;
      }
      return;
    }

    // If campaign is scheduled, show confirmation modal
    if (campaign.status === CampaignsBulkStatusEnum.SCHEDULED && confirm?.require) {
      confirm.require({
        group: 'bulkCampaignsConfirm',
        message: t('campaignsBulkList.editScheduled.message'),
        header: t('campaignsBulkList.editScheduled.header'),
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('campaignsBulkList.editScheduled.accept'),
        rejectLabel: t('campaignsBulkList.editScheduled.reject'),
        accept: async () => {
          await openBulkCampaignEditor(campaign.id, campaign.status, campaign.name, undefined, options.onRefresh);
        },
      });
      return;
    }

    // Use shared function to open the editor
    await openBulkCampaignEditor(campaign.id, campaign.status, campaign.name, undefined, options.onRefresh);
  };

  /**
   * Row actions - triggered from individual row menu
   * Actions visibility depends on campaign status
   */
  const rowActions = computed<RowActionOption[]>(() => [
    // Preview (analytics view)
    {
      label: t('campaignsBulkList.actions.preview'),
      icon: 'far fa-eye',
      visible: (rowData: BulkCampaignData) => canPreview(rowData.status) && findPermission('bulk_campaigns.view'),
      command: (rowData: BulkCampaignData) => {
        handlePreview(rowData);
      },
    },
    // Stop sending (suspend)
    {
      label: t('campaignsBulkList.actions.suspend'),
      icon: 'far fa-hand-paper',
      class: 'p-button-danger',
      visible: (rowData: BulkCampaignData) => canSuspend(rowData.status) && findPermission('bulk_campaigns.stop_send'),
      command: (rowData: BulkCampaignData) => {
        if (options.suspendRef.value?.open) {
          options.suspendRef.value.open(buildPayload(rowData));
        }
      },
    },
    // Edit
    {
      label: t('campaignsBulkList.actions.edit'),
      icon: 'far fa-edit',
      visible: (rowData: BulkCampaignData) => canEdit(rowData.status) && findPermission('bulk_campaigns.edit'),
      command: (rowData: BulkCampaignData) => {
        handleEdit(rowData);
      },
    },
    // Duplicate
    {
      label: t('campaignsBulkList.actions.duplicate'),
      icon: 'far fa-clone',
      visible: () => findPermission('bulk_campaigns.duplicate'),
      command: (rowData: BulkCampaignData) => {
        if (options.duplicateRef.value?.open) {
          options.duplicateRef.value.open(buildPayload(rowData));
        }
      },
    },
    // Archive
    {
      label: t('campaignsBulkList.actions.archive'),
      icon: 'far fa-archive',
      visible: (rowData: BulkCampaignData) => canArchive(rowData.status) && findPermission('bulk_campaigns.archive'),
      command: (rowData: BulkCampaignData) => {
        if (options.archiveRef.value?.open) {
          options.archiveRef.value.open(buildPayload(rowData));
        }
      },
    },
    // Delete
    {
      label: t('campaignsBulkList.actions.delete'),
      icon: 'far fa-trash',
      visible: (rowData: BulkCampaignData) => canDelete(rowData.status) && findPermission('bulk_campaigns.delete'),
      command: (rowData: BulkCampaignData) => {
        if (options.deleteRef.value?.open) {
          options.deleteRef.value.open(buildPayload(rowData));
        }
      },
    },
  ]);

  /**
   * Grouped actions - triggered from selection toolbar
   */
  const groupedActions = computed<GroupedActionOption[]>(() => {
    const actions: GroupedActionOption[] = [];

    if (isWorkshopAccount.value) {
      actions.push({
        label: t('campaignsBulkList.groupedActions.publishToGallery'),
        icon: 'fa fa-cloud-arrow-up',
        command: (selection: SelectionInfo) => {
          if (options.onPublishToGallery) {
            options.onPublishToGallery(selection);
          }
        },
      });
    }

    return actions;
  });

  return {
    rowActions,
    groupedActions,
    canPreview,
    canEdit,
    canDelete,
    canArchive,
    canSuspend,
  };
}
